import { Text } from '@thanx/uikit/text'
import React from 'react'
import { useStyletron } from 'styletron-react'

export type FieldGridItem = {
  label: string
  value: string
}

type Props = {
  items: FieldGridItem[]
  // Required: the list's accessible name. Every consumer already has a
  // translated section heading to hand, and keeping it required means this
  // shared component owns no locale keys of its own.
  ariaLabel: string
}

// Generic 2-column responsive grid of label/value pairs (bold dark label above
// a grey value). Shared by the RecoveryAI case detail (Customer information /
// Case details panels) and the Reports order-rating detail page. Strings arrive
// already-translated via props, so this component renders no locale text.
const FieldGrid = ({ items, ariaLabel }: Props) => {
  const [css] = useStyletron()

  const listClass = css({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '24px',
    rowGap: '16px',
    margin: 0,
  })

  const valueClass = css({
    margin: 0,
  })

  return (
    <dl className={listClass} aria-label={ariaLabel}>
      {items.map((item, index) => (
        // Composite key (index + label): labels arrive already-translated, and
        // two could collide in some locale — React would silently dedup and drop
        // a row. Matches the ReceiptModal line-item key idiom.
        <div key={`${index}-${item.label}`}>
          <dt>
            <Text.BodyText4 bold color="grey90" tag="span">
              {item.label}
            </Text.BodyText4>
          </dt>
          <dd className={valueClass}>
            <Text.BodyText4 color="grey70" tag="span">
              {item.value}
            </Text.BodyText4>
          </dd>
        </div>
      ))}
    </dl>
  )
}

export default FieldGrid
